import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, Checkbox, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { loginApi } from '../../services/api';
import { encryptMD5 } from '../../utils/crypto';
import './styles.css';

const { Title, Text } = Typography;

interface LoginFormValues {
  account: string;
  password: string;
  remember: boolean;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    try {
      const savedAccount = localStorage.getItem('savedAccount');
      if (savedAccount) {
        form.setFieldsValue({ 
          account: savedAccount,
          remember: true
        });
      }
    } catch (error) {
      console.error('Error loading saved credentials:', error);
    }
  }, [form]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const onFinish = async (values: LoginFormValues) => {
    try {
      setLoading(true);
      const loginData = { ...values };
      loginData.password = encryptMD5(values.password);
      const response = await loginApi(loginData.account, loginData.password);
      
      if (response.data.code === 0 && response.data.data) {
        const { access_token, uid, realName } = response.data.data;
        try {
          localStorage.setItem('token', access_token);
          localStorage.setItem('user', JSON.stringify({ uid, realName }));
          if (values.remember) {
            localStorage.setItem('savedAccount', values.account);
          } else {
            localStorage.removeItem('savedAccount');
          }
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
        }
        messageApi.success('登录成功！');
        navigate('/projects');
      } else {
        messageApi.error(response.data.msg || '登录失败');
      }
    } catch (error) {
      console.error('登录错误:', error);
      messageApi.error('登录失败，请检查网络连接后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.stopPropagation();
    messageApi.info('请联系系统管理员重置密码');
  };

  return (
    <div className="login-container">
      {contextHolder}
      <div className="login-wrapper">
        {/* Left branding panel */}
        <div className="login-branding">
          <div className="branding-content">
            <Title level={2} className="brand-title">综合数据管理系统</Title>
            <Text className="brand-subtitle">Integrated Data Management System</Text>
            <div className="brand-features">
              <div className="feature-item">
                <span className="feature-dot" />
                <span>安全可靠</span>
              </div>
              <div className="feature-item">
                <span className="feature-dot" />
                <span>高效稳定</span>
              </div>
              <div className="feature-item">
                <span className="feature-dot" />
                <span>智能管理</span>
              </div>
            </div>
          </div>
          <div className="branding-decoration" />
        </div>

        {/* Right login form panel */}
        <div className="login-form-panel">
          <div className="form-inner">
            <div className="login-header">
              <Title level={3} className="form-title">欢迎登录</Title>
              <Text type="secondary">请输入您的账号和密码</Text>
            </div>

            <Form
              form={form}
              name="login"
              initialValues={{ remember: false }}
              onFinish={onFinish}
              size="large"
              layout="vertical"
              autoComplete="off"
            >
              <Form.Item
                name="account"
                rules={[{ required: true, message: '请输入账号!' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="账号" 
                  autoComplete="off"
                  onChange={handleInputChange}
                  onPaste={(e) => e.stopPropagation()}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                  autoComplete="off"
                  onChange={handleInputChange}
                  onPaste={(e) => e.stopPropagation()}
                />
              </Form.Item>

              <Form.Item>
                <div className="login-form-options">
                  <Form.Item name="remember" valuePropName="checked" noStyle>
                    <Checkbox>记住账号</Checkbox>
                  </Form.Item>
                  <a className="login-form-forgot" onClick={handleForgotPassword}>
                    忘记密码?
                  </a>
                </div>
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  className="login-button" 
                  loading={loading}
                  block
                >
                  登 录
                </Button>
              </Form.Item>
            </Form>

            <div className="login-footer">
              <Text type="secondary">© 2026 数据管理系统</Text>
              <br />
              <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
                辽ICP备12014462号-7
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
